import { format, isAfter, isBefore, isToday, isTomorrow } from "date-fns";
import { Calendar, Clock, Lock, LockKeyhole, RefreshCcw } from "lucide-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { cn } from "@/lib/utils";
import { PublicSession } from "@classview/shared-types";

type SessionCardProps = {
  session: PublicSession;
};

export function SessionCard(props: SessionCardProps) {
  const { session } = props;
  const { organisation } = useOrganisation();
  const sessionDuration = `${format(session.start, "HH:mm")} - ${format(
    session.end,
    "HH:mm",
  )}`;

  const sessionDate = session.start;
  const formattedDate = isToday(sessionDate)
    ? "Today"
    : isTomorrow(sessionDate)
      ? "Tomorrow"
      : format(sessionDate, "PPP");

  /**
   * If the session was created in a different organisation than the current organisation,
   * then it is an external session
   */
  const isExternal = session.hostOrganisationId !== organisation.id;

  const isNow =
    isAfter(new Date(session.end), new Date()) &&
    isBefore(new Date(session.start), new Date());

  return (
    <div
      className={cn(
        "space-y-4 border-l-2 pl-4",
        isExternal ? "border-warning" : "border-primary",
      )}
    >
      <TooltipProvider>
        {isExternal && (
          <Badge variant="outline">
            <span className="flex items-center">
              <span className="mr-2 inline-block size-2 rounded-full bg-warning" />
              External
            </span>
          </Badge>
        )}
        <p className="text-md font-semibold text-foreground">
          {isNow && (
            <span className="relative mr-2 inline-flex size-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75"></span>
              <span className="relative inline-flex size-2 rounded-full bg-green-500"></span>
            </span>
          )}
          {session.name}
        </p>

        <ul className="flex flex-row space-x-2">
          <li>
            <p className="flex items-center text-xs text-muted">
              <Clock
                className="mr-1 size-3 text-card-foreground"
                aria-hidden={true}
              />
              {sessionDuration}
            </p>
          </li>
          <li>
            <p className="flex items-center text-xs text-muted">
              <Calendar
                className="mr-1 size-3 text-card-foreground"
                aria-hidden={true}
              />
              {formattedDate}
            </p>
          </li>
          {session.isPrivate && (
            <li>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className="flex size-5 items-center justify-center p-0 text-muted-foreground"
                  >
                    {session.isConfidential ? (
                      <LockKeyhole className="size-3" />
                    ) : (
                      <Lock className="size-3" />
                    )}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="text-sm font-medium">
                    {session.isConfidential
                      ? "This session is confidential!"
                      : "This session is private!"}
                  </p>
                  <p className="text-xs">
                    {session.isConfidential
                      ? "Only invited attendees and admins can join"
                      : "Only invited attendees can join"}
                  </p>
                </TooltipContent>
              </Tooltip>
            </li>
          )}
          {session.recurringDetails && (
            <li>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className="flex size-5 items-center justify-center p-0 text-muted-foreground"
                  >
                    <RefreshCcw className="size-3" />
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="text-sm font-medium">
                    This session is recurring
                  </p>
                </TooltipContent>
              </Tooltip>
            </li>
          )}
        </ul>
        <div className="flex items-center">
          <Avatar>
            {/* <AvatarImage src="https://github.com/shadcn.png" /> */}
            <AvatarFallback className="bg-border capitalize text-muted">
              {session.organiser.firstName[0]}
              {session.organiser.lastName[0]}
            </AvatarFallback>
          </Avatar>
          <div className="ml-2">
            <p className="text-sm leading-tight text-foreground">
              {session.organiser.firstName} {session.organiser.lastName}
            </p>
            <p className="text-sm text-muted">Host</p>
          </div>
        </div>
      </TooltipProvider>
    </div>
  );
}
